import React from 'react'
import styled from 'styled-components'
import { Snackbar } from '@mui/material'

/* ================= STYLES ================= */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding-top: ${({ $page }) => ($page ? "24px" : "0")};
  @media (max-width: 960px) {
    padding-left: 0px;
    padding-right: 0px;
  }
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: min(1440px, 100%);
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 48px 80px;
  box-sizing: border-box;
  gap: 12px;
  @media (max-width: 640px) {
    padding: 0 16px 64px;
  }
`

const Title = styled.h2`
  font-size: clamp(28px, 5vw, 42px);
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
`

const Desc = styled.p`
  font-size: 16px;
  text-align: center;
  max-width: 640px;
  padding: 0 16px;
  color: ${({ theme }) => theme.text_secondary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 16px;
  }
`

const ContactForm = styled.form`
  width: min(600px, calc(100% - 32px));
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.card};
  padding: 32px 20px;
  border-radius: 16px;
  border: 1px solid #ece7f6;
  box-shadow: 0 12px 32px rgba(80, 40, 140, 0.08);
  margin-top: 28px;
  gap: 12px;
  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`

const ContactTitle = styled.div`
  font-size: 24px;
  margin-bottom: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`

const ContactInput = styled.input`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactInputMessage = styled.textarea`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  resize: none;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactButton = styled.input`
  width: 100%;
  text-align: center;
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  padding: 13px 16px;
  margin-top: 8px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.white};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`

/* ================= COMPONENT ================= */

const Contact = ({ intro, page = false }) => {
  const [open, setOpen] = React.useState(false)
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleWhatsAppSend = (e) => {
    e.preventDefault()

    if (!formData.name || !formData.message) return

    const phoneNumber = '9779826149719' // 🔴 REPLACE with your WhatsApp number

    const text = `
Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

Message:
${formData.message}
    `

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`
    window.open(url, '_blank')

    setOpen(true)
    setFormData({ name: '', email: '', subject: '', message: '' })
  }

  return (
    <Container id="contact" $page={page}>
      <Wrapper>
        <Title>Contact</Title>
        <Desc>{intro || "Send a message and I will open WhatsApp with the details filled in."}</Desc>

        <ContactForm onSubmit={handleWhatsAppSend}>
          <ContactTitle>WhatsApp Me</ContactTitle>

          <ContactInput
            placeholder="Your Name"
            aria-label="Your name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <ContactInput
            placeholder="Your Email"
            aria-label="Your email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />

          <ContactInput
            placeholder="Subject"
            aria-label="Subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
          />

          <ContactInputMessage
            placeholder="Message"
            aria-label="Message"
            rows="4"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
          />

          <ContactButton type="submit" value="Send via WhatsApp" />
        </ContactForm>

        <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={() => setOpen(false)}
          message="Redirecting to WhatsApp..."
        />
      </Wrapper>
    </Container>
  )
}

export default Contact
